import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";
import { formatDateLabel } from "../common/reporting";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [show, setShow] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [failedSaveProject, setFailedSaveProject] = useState("");
  const [successfulSaveProject, setSuccessfulSaveProject] = useState("");
  const user = AuthService.getCurrentUser();
  const clientPrimaryKeyId = user && user.clients && user.clients[0] && user.clients[0].id;
  const clientId = user && user.clients && user.clients[0] && user.clients[0].companyCode;
  const userId = user && user.user && user.user.id;

  const now = () => {
    const date = new Date();
    const day = `${date.getUTCDate()}`.padStart(2, "0");
    const month = `${date.getUTCMonth() + 1}`.padStart(2, "0");
    const year = date.getUTCFullYear();
    const hours = `${date.getUTCHours()}`.padStart(2, "0");
    const minutes = `${date.getUTCMinutes()}`.padStart(2, "0");
    const seconds = `${date.getUTCSeconds()}`.padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const loadProjects = useCallback(() => {
    DataService.fetchProjects(clientPrimaryKeyId).then((response) => {
      setProjects(response.data);
    });
  }, [clientPrimaryKeyId]);

  useEffect(() => {
    if (!clientPrimaryKeyId) {
      return;
    }

    loadProjects();
  }, [clientPrimaryKeyId, loadProjects]);

  const metrics = useMemo(() => {
    return {
      totalProjects: projects.length,
      activeProjects: projects.filter((project) => Number(project.status) === 1).length,
      latestProject: projects[projects.length - 1],
    };
  }, [projects]);

  const handleClose = () => {
    setShow(false);
    setSuccessfulSaveProject("");
    setFailedSaveProject("");
    setProjectName("");
  };

  const handleSubmit = () => {
    const project = {
      id: 0,
      clientId: clientPrimaryKeyId,
      projectName,
      dateCreated: now(),
      createUser: userId,
      status: 1,
    };

    DataService.createProject(project).then(
      (response) => {
        if (response.data.status === "200") {
          setSuccessfulSaveProject("Project saved successfully.");
          setProjectName("");
          setFailedSaveProject("");
          loadProjects();
        } else {
          setFailedSaveProject(response.data.statusMessage);
          setSuccessfulSaveProject("");
        }
      },
      () => {
        setFailedSaveProject("Server error.");
        setSuccessfulSaveProject("");
      }
    );
  };

  if (!clientId) {
    return (
      <section className="empty-state empty-state--large">
        <span className="bi bi-kanban" aria-hidden="true" />
        <h4>No client profile linked</h4>
        <p>Link this user to a client before creating operational projects.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-stack">
      <section className="metrics-grid">
        <article className="metric-card">
          <p className="metric-card__label">Projects</p>
          <h4>{metrics.totalProjects}</h4>
          <span>Payment initiatives currently available for transaction tagging and reporting.</span>
        </article>
        <article className="metric-card metric-card--highlight">
          <p className="metric-card__label">Active</p>
          <h4>{metrics.activeProjects}</h4>
          <span>Projects currently marked active and available for new transaction batches.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Latest project</p>
          <h4>{metrics.latestProject ? metrics.latestProject.projectName : "—"}</h4>
          <span>Most recently created project in the current client workspace.</span>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Project governance</p>
            <h4>Operational projects</h4>
          </div>
          <Button variant="primary" onClick={() => setShow(true)}>
            Add project
          </Button>
        </div>
        {projects.length ? (
          <div className="table-responsive portal-table-wrap">
            <table className="table portal-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Project name</th>
                  <th>Status</th>
                  <th>Created date</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td>{project.id}</td>
                    <td>{project.projectName}</td>
                    <td>
                      <span className={`status-badge status-badge--${Number(project.status) === 1 ? "success" : "neutral"}`}>
                        {Number(project.status) === 1 ? "Active" : `Status ${project.status}`}
                      </span>
                    </td>
                    <td>{formatDateLabel(project.dateCreated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state empty-state--compact">
            <span className="bi bi-kanban" aria-hidden="true" />
            <h5>No projects yet</h5>
            <p>Create the first project to organize transaction batches and reporting.</p>
          </div>
        )}
      </section>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-supporting-text">
            Use projects to segment payment activity by initiative, campaign, or business line.
          </p>
          <Form>
            <Form.Group className="mb-0" controlId="createProjectName">
              <Form.Label>Project name</Form.Label>
              <Form.Control type="text" value={projectName} onChange={(event) => setProjectName(event.target.value)} />
            </Form.Group>
          </Form>
          {failedSaveProject ? <Alert variant="danger" className="mt-3 mb-0">{failedSaveProject}</Alert> : null}
          {successfulSaveProject ? <Alert variant="success" className="mt-3 mb-0">{successfulSaveProject}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save project
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Projects;
